import { useRef, useEffect, useState, useCallback, Fragment } from 'react';
import { Player } from '@lordicon/react';
import styles from './decisionScreen.module.css';
import FormModal from '../../components/formModal/FormModal';
import jumpIcon from '../../icons/wired-outline-3141-female-jumping-hover-jump (1).json';
import bookIcon from '../../icons/wired-outline-112-book-morph-open.json';
import bankIcon from '../../icons/wired-outline-1010-department-hover-pinch (1).json';
import useScrollReveal from '../../hooks/useScrollReveal';

/* ── Animated lordicon  -  plays when activated ── */
function LordIcon({ icon, colorize = '#724121', size = '100%', className, play }) {
  const ref = useRef(null);
  const hasPlayed = useRef(false);
  useEffect(() => {
    if (play && !hasPlayed.current) {
      hasPlayed.current = true;
      const t = setTimeout(() => ref.current?.playFromBeginning(), 100);
      return () => clearTimeout(t);
    }
  }, [play]);
  const handleComplete = () => {
    setTimeout(() => ref.current?.playFromBeginning(), 3500);
  };
  return (
    <div className={className}>
      <Player icon={icon} ref={ref} size={size} colorize={colorize} stroke="light" onComplete={handleComplete} />
    </div>
  );
}

/* ══════════════════════════════════
   STORY CARDS  -  centered thread with scroll reveal
   ══════════════════════════════════ */
function DreamTimeline({ steps }) {
  const [activeStep, setActiveStep] = useState(-1);
  const beadRefs = useRef([]);
  const lineRef = useRef(null);
  const timelineRef = useRef(null);

  /* Scroll-driven: calculate which step is active */
  useEffect(() => {
    let ticking = false;
    const update = () => {
      if (!timelineRef.current) return;
      const viewMid = window.innerHeight * 0.6;
      let current = -1;
      beadRefs.current.forEach((el, i) => {
        if (!el) return;
        const rect = el.getBoundingClientRect();
        if (rect.top < viewMid) current = i;
      });
      setActiveStep(current);
      ticking = false;
    };
    const onScroll = () => {
      if (!ticking) {
        requestAnimationFrame(update);
        ticking = true;
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    update();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  /* Animate thread progress  -  follows active bead */
  useEffect(() => {
    if (!lineRef.current || !timelineRef.current) return;
    if (activeStep < 0) {
      lineRef.current.style.height = '0px';
      return;
    }
    const timelineRect = timelineRef.current.getBoundingClientRect();
    const targetEl = beadRefs.current[activeStep];
    if (!targetEl) return;
    const targetRect = targetEl.getBoundingClientRect();
    const h = targetRect.top - timelineRect.top + targetRect.height * 0.5;
    lineRef.current.style.height = `${Math.max(0, h)}px`;
  }, [activeStep]);

  const setBeadRef = useCallback((el, i) => {
    beadRefs.current[i] = el;
  }, []);

  return (
    <div className={styles.dreamTimeline} ref={timelineRef}>
      {/* Background thread (always visible, dim) */}
      <div className={styles.dtThread} />
      {/* Active progress thread (grows with scroll) */}
      <div className={styles.dtThreadProgress} ref={lineRef} />

      {steps.map((step, i) => {
        const isActive = i <= activeStep;
        const isCurrent = i === activeStep;
        return (
          <Fragment key={i}>
            {/* Bead  -  icon circle on the thread */}
            <div
              ref={el => setBeadRef(el, i)}
              className={`${styles.dtBead} ${isActive ? styles.dtBeadActive : ''} ${isCurrent ? styles.dtBeadCurrent : ''}`}
            >
              <LordIcon
                icon={step.icon}
                className={styles.dtBeadIcon}
                colorize={isActive ? '#FAF0EB' : '#8C572A'}
                play={isActive}
              />
            </div>

            {/* Story card */}
            <div className={`${styles.dtCard} ${isActive ? styles.dtCardActive : ''}`}>
              <div className={styles.dtCardShimmer} />
              <div className={styles.dtCardInner}>
                <h3 className={styles.dtTitle}>{step.title}</h3>
                {step.description && <p className={styles.dtDesc}>{step.description}</p>}
              </div>
            </div>
          </Fragment>
        );
      })}
    </div>
  );
}

/* ── Curly arrow connector ── */
const CurlyArrow = () => (
  <svg className={styles.curlyArrow} width="20" height="38" viewBox="0 0 20 38" fill="none">
    <path
      d="M10 0 Q2 9.5 10 19 Q18 28.5 10 38"
      stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"
    />
    <path
      d="M6 32 L10 38 L14 32"
      stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"
    />
  </svg>
);

/* ── Data ── */
const realityLines = [
  'את קמה בבוקר, נוסעת לעבודה',
  'עושה משמרת של 8-9 שעות',
  'חוזרת הביתה שחוקה',
  'מסתכלת על התלוש ושואלת את עצמך:',
];


const dreamSteps = [
  {
    icon: jumpIcon,
    title: 'את קמה בבוקר בלי לחץ ובלי לרוץ לשום מקום.',
    description: 'היום מתחיל בקצב שלך. את בוחרת אם לעבוד מהבית או מכל מקום שנוח לך, ואין אף אחד שמחליט בשבילך מתי להתחיל ומתי לסיים.',
  },
  {
    icon: bookIcon,
    title: 'את פותחת את היומן ויש בו לקוחות שמחכות לך.',
    description: 'את לא רודפת אחרי אף אחת. הן מגיעות כי מישהי המליצה, כי ראו את העבודות שלך, כי הן כבר היו בטיפול ורוצות להגיע שוב. כל טיפול מתומחר כמו שמגיע לך, ואת בונה לוז שמתאים לחיים שלך ולא להפך. העסק שלך עובד כי את טובה, ואנשים מדברים.',
  },
  {
    icon: bankIcon,
    title: 'בסוף החודש את נכנסת לחשבון הבנק ומרגישה סיפוק.',
    description: 'אין תלוש שמישהו אחר קבע. את החלטת כמה לגבות וכמה לעבוד, ואת רואה את התוצאה. אין תקרה שמגבילה אותך ואין מנהל שמסביר לך מה נכון לעשות. הזמן שלך, ההכנסה שלך והבחירות שלך בידיים שלך.',
  },
];

/* ══════════════════════════════════ */
function DecisionScreen() {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const sectionRef = useRef(null);
  useScrollReveal(sectionRef);
  return (
    <section className={styles.screen} ref={sectionRef}>

      {/* BG */}
      <div className={styles.glowTR} />
      <div className={styles.glowBL} />
      {[...Array(8)].map((_, i) => (
        <div key={i} className={`${styles.brownSparkle} ${styles[`bs${i + 1}`]}`} />
      ))}

      <div className={styles.container}>

        {/* ═══════ PART 1 ═══════ */}
          <div className={styles.sectionHeader} data-sr="0">
            <h2 className={styles.mainTitle}>אז למה שנדבר דווקא עכשיו?</h2>
            <p className={styles.headerDesc}>כי אני יודעת בדיוקקק איך השגרה שלך נראית היום..:</p>
          </div>

        {/* ── Reality Loop ── */}
        <div className={styles.realityLoop}>
          {realityLines.map((line, i) => (
            <Fragment key={i}>
                <div className={styles.realityPill} data-sr={String(0.08 + i * 0.08)}>
                  <p className={styles.realityText}>{line}</p>
                </div>
              {i < realityLines.length - 1 && (
                <div data-sr={String(0.12 + i * 0.08)} data-sr-y="12">
                  <CurlyArrow />
                </div>
              )}
            </Fragment>
          ))}
        </div>

        {/* ── Punch ── */}
          <div className={styles.punchWrap} data-sr="0.5">
            <span className={styles.punchQuote}>"בשביל מה אני עושה את זה?"</span>
          </div>

          <p className={styles.knowLine} data-sr="0.55">ויודעת בפנים שאת שווה הרבה יותר</p>

        {/* ── Dream section  -  Envelope ── */}
        <div className={styles.dreamEnvelope}>
          <div className={styles.dreamEnvelopeShadow} />
          <div className={styles.dreamZone}>

              <p className={styles.dreamLabel} data-sr="0.08">ועכשיו תדמייני את החיים האלה..</p>

            <DreamTimeline steps={dreamSteps} />

              <div className={styles.statementWrap} data-sr="0.1">
                <p className={styles.statement}>זה לא חלום. <span className={styles.statementHighlight}>זה עניין של החלטה</span></p>
                <p className={styles.statementSub}>וההחלטה הראשונה היא להתחיל - עכשיו</p>
              </div>

              <div className={styles.ctaWrap} data-sr="0.15">
                <button onClick={() => setIsFormOpen(true)} className={styles.ctaBtn}>
                  אני רוצה להתחיל עכשיו!
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M5 12h14M12 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
          </div>
        </div>

      </div>
      <FormModal isOpen={isFormOpen} onClose={() => setIsFormOpen(false)} />
    </section>
  );
}

export default DecisionScreen;
